import { useCallback, useEffect, useRef, useState } from 'react';
import { STORY_DURATION_MS } from '../utils/storyViewer.constants';

export const useStoryProgress = ({
	open,
	slideKey,
	onNext,
	mediaReady,
	paused = false,
	isVideo = false,
}) => {
	const [isPaused, setIsPaused] = useState(false);
	const [segmentProgress, setSegmentProgress] = useState(0);
	const frameRequestRef = useRef(null);
	const storyPausedRef = useRef(false);
	const storyElapsedRef = useRef(0);
	const storyStartedAtRef = useRef(0);
	const onNextRef = useRef(onNext);

	useEffect(() => {
		onNextRef.current = onNext;
	}, [onNext]);

	const stopTimer = () => {
		if (frameRequestRef.current) {
			cancelAnimationFrame(frameRequestRef.current);
			frameRequestRef.current = null;
		}
	};

	const startTimer = () => {
		stopTimer();
		storyStartedAtRef.current = performance.now();

		const tick = (now) => {
			if (storyPausedRef.current) {
				frameRequestRef.current = requestAnimationFrame(tick);
				return;
			}

			const elapsed =
				storyElapsedRef.current + (now - storyStartedAtRef.current);
			const ratio = Math.min(1, Math.max(0, elapsed / STORY_DURATION_MS));
			setSegmentProgress(ratio * 100);

			if (elapsed >= STORY_DURATION_MS) {
				onNextRef.current?.({ source: 'auto' });
				return;
			}

			frameRequestRef.current = requestAnimationFrame(tick);
		};

		frameRequestRef.current = requestAnimationFrame(tick);
	};

	useEffect(() => {
		if (!open) {
			stopTimer();
			storyPausedRef.current = false;
			setIsPaused(false);
			storyElapsedRef.current = 0;
			setSegmentProgress(0);
			return;
		}

		if (paused || !mediaReady) {
			stopTimer();

			if (!mediaReady) {
				storyElapsedRef.current = 0;
				setSegmentProgress(0);
			}

			return;
		}

		storyPausedRef.current = false;
		setIsPaused(false);
		storyElapsedRef.current = 0;
		setSegmentProgress(0);

		if (isVideo) {
			return stopTimer;
		}

		startTimer();

		return stopTimer;
	}, [open, slideKey, mediaReady, paused, isVideo]);

	const handleVideoProgress = useCallback(
		(ratio) => {
			if (!mediaReady || storyPausedRef.current) {
				return;
			}

			const nextProgress = Math.min(100, Math.max(0, ratio * 100));

			setSegmentProgress((current) =>
				Math.abs(current - nextProgress) < 0.35 ? current : nextProgress,
			);
		},
		[mediaReady],
	);

	const handleVideoEnded = useCallback(() => {
		if (!mediaReady || storyPausedRef.current) {
			return;
		}

		onNextRef.current?.({ source: 'auto' });
	}, [mediaReady]);

	const pauseProgress = () => {
		storyPausedRef.current = true;
		setIsPaused(true);
		storyElapsedRef.current +=
			performance.now() - storyStartedAtRef.current;
		stopTimer();
	};

	const resumeProgress = () => {
		if (paused || !mediaReady) {
			return;
		}

		storyPausedRef.current = false;
		setIsPaused(false);

		if (isVideo) {
			return;
		}

		storyStartedAtRef.current = performance.now();
		startTimer();
	};

	return {
		isPaused,
		segmentProgress,
		storyPausedRef,
		pauseProgress,
		resumeProgress,
		handleVideoProgress,
		handleVideoEnded,
	};
};
